import React from 'react';
// Uitk components
import { UitkCard, UitkCardLink, UitkCardContentSection } from 'uitk-react-cards';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLayoutGrid } from 'uitk-react-layout-grid';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkFigure, UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkRating } from 'uitk-react-rating';
import { UitkLayoutPosition } from 'uitk-react-layout-position';

import { LazyLoadedImage } from 'src/components/common/modules/lazy/lazy-loaded-image';
import { ImageBadgesForPropertySearch } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-image-badges';
import { SeparatorPropertySearch, SeparatorProps } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-separator';
import { PricePropertyComponent } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-price-component';
import { TrackedLink } from 'src/components/common/modules/analytics/tracked-link';
import { Action } from 'src/components/common/modules/analytics/analytics-utils';
import { checkValue } from 'src/components/common/helper';

// GA Logger
import gaLogger from 'src/utils/ga-logger';

type HotelCardProps = {
  dealUrl?: string;
  offerBadge?: string;
  propertyImageUrl?: string;
  propertyImageDescription?: string;
  name?: string;
  starRating?: string;
  location?: string;
  guestReviewRating?: string;
  guestReviewCount?: string;
  separatorProps?: SeparatorProps;
  offerSummary?: string;
  offerPrice?: any;
  offerId?: string;
  propertyDetailsLink?: {
    referrerId?: string;
    linkName?: string;
  };
};

const HotelCard = (hotelCardProps: HotelCardProps, index: number, tracking: string, offerDate: string) => {
  const {
    dealUrl,
    offerBadge,
    propertyImageUrl,
    propertyImageDescription,
    name,
    starRating,
    location,
    guestReviewRating,
    guestReviewCount,
    separatorProps,
    offerSummary,
    offerPrice,
    offerId,
    propertyDetailsLink,
  } = hotelCardProps;

  const dealOnClickEvent = (module) => {
    gaLogger.logGa('Hotel widget', 'Hotel card click', module);
  };

  const hotelDealUrl =
    !checkValue(dealUrl) || dealUrl.indexOf('mcicid') > -1
      ? dealUrl
      : dealUrl.indexOf('?') > -1
      ? dealUrl + '&mcicid=' + tracking
      : dealUrl + '?mcicid=' + tracking;

  return (
    <UitkCard className="HotelInventoryCard" border={true} data-testid="HotelInventoryCard">
      <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
        <UitkSpacing margin={{ blockstart: 'three' }}>
          <UitkLayoutPosition type="absolute" position={{ left: 'three', zindex: 'layer2' }} cloneElement>
            <UitkLayoutFlex space="two">
              <ImageBadgesForPropertySearch message={offerBadge} />
            </UitkLayoutFlex>
          </UitkLayoutPosition>
        </UitkSpacing>
        <LazyLoadedImage src={propertyImageUrl} alt={propertyImageDescription} />
      </UitkFigure>
      <UitkCardContentSection className="HotelInventoryCardSection">
        <UitkSpacing padding={{ blockend: 'four' }}>
          <UitkLayoutPosition type="relative">
            <div>
              <UitkSpacing padding={{ blockend: 'half' }}>
                <UitkHeading overflow="truncate" tag="h3" size={5}>
                  {name}
                </UitkHeading>
              </UitkSpacing>
              <UitkRating rating={`${starRating}`} />
              <UitkText size={300}>{location}</UitkText>
              {guestReviewRating && (
                <UitkLayoutPosition type="absolute" position={{ bottom: 'minus-two' }} cloneElement>
                  <div className="reviewRating">
                    <UitkLayoutFlex>
                      <UitkSpacing padding={{ inlineend: 'one' }}>
                        <UitkText weight="bold" size={300}>
                          {guestReviewRating}
                        </UitkText>
                      </UitkSpacing>
                      <UitkText size={300} className="pwa-theme--grey-700">
                        {guestReviewCount}
                      </UitkText>
                    </UitkLayoutFlex>
                  </div>
                </UitkLayoutPosition>
              )}
            </div>
          </UitkLayoutPosition>
        </UitkSpacing>
      </UitkCardContentSection>
      <UitkLayoutPosition type="relative">
        <UitkCardContentSection className="HotelInventoryCardSection">
          <SeparatorPropertySearch {...separatorProps} />
          {offerSummary && (
            <UitkLayoutPosition type="absolute" position={{ top: 'six' }} cloneElement className='hotel-card-offer-summery'>
              <UitkText size={300} weight="bold" theme="positive">
                {offerSummary}
              </UitkText>
            </UitkLayoutPosition>
          )}
          <UitkLayoutGrid alignItems="end" columns={1}>
            <>
              <div className="uitk-type-right uitk-price-container">
                <PricePropertyComponent
                  offer={offerPrice}
                  cardIndex={index}
                  campaignId={tracking}
                  moduleName={`${tracking}.hotel`}
                  cardClicked={() => {
                    dealOnClickEvent(`${tracking}.hotel.${offerId}`);
                  }}
                />
              </div>
            </>
          </UitkLayoutGrid>
          <UitkLayoutGrid className="hotel-card-date-container" alignItems="end" columns={1}>
            <>
              <UitkText align="left" size={200}>
                {offerDate}
              </UitkText>
            </>
          </UitkLayoutGrid>
        </UitkCardContentSection>
      </UitkLayoutPosition>
      <UitkCardLink>
        {propertyDetailsLink && (
          <TrackedLink
            className="ga4-deals-card"
            moduleName={`${tracking}.deal`}
            action={Action.CLICK}
            href={hotelDealUrl}
            rfrr={`${tracking}.${propertyDetailsLink?.referrerId}`}
            mcicid={`mcicid=${tracking}`}
            onClick={() => dealOnClickEvent(`${tracking}.hotel.${offerId}`)}
            target={'_blank'}
            aria-label={`${name}`}
          >
            {tracking}-{propertyDetailsLink?.linkName}
          </TrackedLink>
        )}
      </UitkCardLink>
    </UitkCard>
  );
};

export { HotelCard, HotelCardProps };